import dayjs from 'dayjs'
import { Column, Day, UsersProject, WorkDay, WorkEntry } from '../types'
import { HolidayChecker } from './holidaysUtils'

export const isRemote = (entries: WorkEntry[] | undefined): boolean => {
  if (entries === undefined) {
    return false
  }

  let remoteHours = 0
  let officeHours = 0

  entries.forEach(function (obj) {
    if (obj.hasOwnProperty('remote')) {
      if (obj.remote) {
        remoteHours += obj.hours || 0
      } else {
        officeHours += obj.hours || 0
      }
    }
  })

  return remoteHours > officeHours
}

export const roundHours = (durationInHours: number) => {
  if (Number.isInteger(durationInHours)) {
    return durationInHours
  }

  return Math.round(durationInHours * 100) / 100
}

export const generateColumns = (worklog: WorkDay[] | undefined, vacationId: string): Column[] => {
  let columns: Column[] = []

  if (worklog === undefined) return columns

  let projects: UsersProject[] = getUsersProjects(worklog)
  projects.forEach((p, i) => {
    let header: Column = {
      id: i,
      projectId: p.id,
      label: p.name,
      sum: sumProjectPerMonth(worklog, p.id),
    }

    if (p.id !== vacationId && header !== undefined) columns.push(header)
  })

  return columns === undefined ? [] : columns
}

export const getDaysProjects = (entries: WorkEntry[]) => {
  let projects: UsersProject[] = entries?.map(e => {
    return {
      id: e.project.id,
      name: e.project.name,
    }
  })

  return projects
}

export const getUsersProjects = (worklog: WorkDay[]): UsersProject[] => {
  let projects: UsersProject[] = []

  worklog?.forEach(day => {
    projects = projects.concat(getDaysProjects(day.entries))
  })

  return [...new Map(projects.map(item => [item['id'], item])).values()]
}

export const getDaysOfTheMonth = (date: dayjs.Dayjs) => {
  let rows: Day[] = []

  for (let i = 1; i <= date.daysInMonth(); i++) {
    let day = date.set('date', i)
    let isWeekDay = day.isBusinessDay()

    rows.push({
      id: i,
      date: day,
      isWeekDay: isWeekDay,
    })
  }

  return rows
}

export const getLowestWorkTime = (entries: WorkEntry[]): number => {
  if (entries.length === 0) return 0
  return entries.reduce((prev, curr) => (prev.hours < curr.hours ? prev : curr)).hours
}

export const getHighestWorkTime = (entries: WorkEntry[]): number => {
  if (entries.length === 0) return 0
  return entries.reduce((prev, curr) => (prev.hours > curr.hours ? prev : curr)).hours
}

export const getDurationString = (durationInHours: number) => {
  const isNegative = durationInHours < 0
  const absHours = Math.abs(durationInHours)

  const totalMinutes = Math.round(dayjs.duration(absHours, 'hours').asMinutes())
  const duration = dayjs.duration(totalMinutes, 'minutes')

  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()
  const prefix = isNegative ? '-' : ''

  if (hours === 0) return `${prefix}${minutes}m`
  if (minutes === 0) return `${prefix}${hours}h`

  return `${prefix}${hours}h ${minutes}m`
}

const isWeekend = (dateString: string): boolean => {
  const date = new Date(dateString)
  const day = date.getDay()
  return day === 0 || day === 6
}

export const getNumericTimeValue = (input: string | null | undefined): number => {
  let value = 0

  if (input === null || input === undefined) return value

  const hoursMatch = input.match(/([1-9]|1[0-9]|2[0-3])h/)
  const minutesMatch = input.match(/30m/)

  if (hoursMatch) {
    value += parseInt(hoursMatch[0].slice(0, -1))
  }

  if (minutesMatch) {
    value += 0.5
  }

  return value
}

export const roundUpToNearestMinute = (durationInHours: number): number => {
  const duration = dayjs.duration(durationInHours, 'hours')
  const totalMinutes = Math.ceil(duration.asMinutes())
  const hours = dayjs.duration(totalMinutes, 'minutes').asHours()
  return roundHours(hours)
}

// SUM PROJECT PER DAY
export const sumProjectPerDay = (
  worklog: WorkDay[] | undefined,
  projectId: string,
  date: string,
  month: string,
  skipHolidays: boolean = false
): number => {
  if (worklog === undefined) return 0
  if (skipHolidays && (isWeekend(date) || HolidayChecker({ date, month }))) return 0

  let sum = worklog
    .find(day => day.date === date)
    ?.entries.filter(entry => entry.project.id === projectId)
    .reduce((accumulator, entry) => {
      return accumulator + entry.hours
    }, 0)

  return sum === undefined ? 0 : sum
}

// SUM PROJECT PER MONTH
export const sumProjectPerMonth = (
  worklog: WorkDay[] | undefined,
  projectId: string,
  skipHolidays: boolean = false
): number => {
  if (worklog === undefined) return 0

  return worklog.reduce((accumulator, day) => {
    return accumulator + sumProjectPerDay(worklog, projectId, day.date, dayjs(day.date).format('YYYY-MM'), skipHolidays)
  }, 0)
}

// SUM TOTAL PER MONTH
export const sumAllPerMonth = (worklog: WorkDay[] | undefined): number => {
  if (worklog === undefined) return 0

  return worklog.reduce((accumulator, day) => {
    return accumulator + sumAllPerDay(worklog, day.date)
  }, 0)
}

// SUM TOTAL PER DAY
export const sumAllPerDay = (worklog: WorkDay[] | undefined, date: string): number => {
  if (worklog === undefined) return 0

  let day = worklog.find(day => day.date === date)
  let projects = [...new Set(day?.entries.map(entry => entry.project.id))]

  return projects.reduce((accumulator, project) => {
    return accumulator + sumProjectPerDay(worklog, project, date, dayjs(date).format('YYYY-MM'))
  }, 0)
}
